/**
 * Custom Refine Data Provider
 * Routes all data operations through the secure serverless API
 *
 * This provider replaces @refinedev/airtable to eliminate the exposed
 * Airtable API key from the browser. All requests go through /api/data/*
 * endpoints which handle Airtable authentication server-side.
 *
 * Phase 2, Session 03d: Provider swap migration
 */

import type { DataProvider, BaseRecord } from "@refinedev/core";
import { useAuthStore } from "@/stores/auth";
import {
  isCallDataTable,
  normalizeCallDataFields,
} from "@/lib/airtable-field-mapper";

// ============================================
// Types
// ============================================

/**
 * API response for list operations
 */
interface ListResponse {
  data: AirtableRecord[];
  total: number;
  offset?: string;
}

/**
 * API response for single record operations
 */
interface SingleResponse {
  data: AirtableRecord;
}

/**
 * API response for delete operations
 */
interface DeleteResponse {
  success: boolean;
  id: string;
}

/**
 * API response for getMany operations
 */
interface ManyResponse {
  data: AirtableRecord[];
}

/**
 * API error response
 */
interface ErrorResponse {
  success: false;
  error: string;
  message: string;
}

/**
 * Airtable record structure
 */
interface AirtableRecord {
  id: string;
  fields: Record<string, unknown>;
  createdTime?: string;
}

/**
 * Refine filter operator type
 */
type FilterOperator =
  | "eq"
  | "ne"
  | "lt"
  | "gt"
  | "lte"
  | "gte"
  | "in"
  | "nin"
  | "contains"
  | "ncontains"
  | "containss"
  | "ncontainss"
  | "between"
  | "nbetween"
  | "null"
  | "nnull"
  | "startswith"
  | "nstartswith"
  | "startswiths"
  | "nstartswiths"
  | "endswith"
  | "nendswith"
  | "endswiths"
  | "nendswiths"
  | "or"
  | "and";

/**
 * Refine filter structure
 */
interface CrudFilter {
  field: string;
  operator: FilterOperator;
  value: unknown;
}

/**
 * Refine sort structure
 */
interface CrudSort {
  field: string;
  order: "asc" | "desc";
}

// ============================================
// Configuration
// ============================================

/**
 * Base URL for the data API
 * Uses relative path so it works in both dev and production
 */
const API_BASE_URL = "/api/data";

// ============================================
// Utility Functions
// ============================================

/**
 * Create Authorization header from current user
 * Encodes user info as base64 JSON for the API
 */
function getAuthHeader(): string | null {
  const user = useAuthStore.getState().user;

  if (!user) {
    return null;
  }

  const payload = JSON.stringify({
    id: user.id,
    email: user.email,
  });

  const encoded = btoa(payload);
  return `Bearer ${encoded}`;
}

/**
 * Make an authenticated fetch request
 * Handles auth header injection and error responses
 */
async function fetchWithAuth<T>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  const authHeader = getAuthHeader();

  if (!authHeader) {
    throw new Error("Not authenticated");
  }

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Authorization: authHeader,
    ...(options.headers as Record<string, string>),
  };

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: "include",
  });

  // Handle 401 - redirect to login
  if (response.status === 401) {
    const { clearAuth } = useAuthStore.getState();
    clearAuth();
    window.location.href = "/login";
    throw new Error("Session expired");
  }

  // Handle error responses
  if (!response.ok) {
    const errorData = (await response.json().catch(() => ({}))) as ErrorResponse;
    throw new Error(
      errorData.message || `Request failed with status ${response.status}`
    );
  }

  return response.json() as Promise<T>;
}

/**
 * Convert Refine filters to Airtable formula
 * Supports eq and contains operators
 */
function filtersToFormula(filters?: CrudFilter[]): string | undefined {
  if (!filters || filters.length === 0) {
    return undefined;
  }

  const conditions: string[] = [];

  for (const filter of filters) {
    const { field, operator, value } = filter;

    // Skip invalid filters
    if (!field || value === undefined || value === null) {
      continue;
    }

    switch (operator) {
      case "eq":
        if (typeof value === "string") {
          conditions.push(`{${field}} = "${value}"`);
        } else if (typeof value === "number") {
          conditions.push(`{${field}} = ${value}`);
        } else if (typeof value === "boolean") {
          conditions.push(`{${field}} = ${value ? "TRUE()" : "FALSE()"}`);
        }
        break;

      case "ne":
        if (typeof value === "string") {
          conditions.push(`{${field}} != "${value}"`);
        } else if (typeof value === "number") {
          conditions.push(`{${field}} != ${value}`);
        }
        break;

      case "contains":
      case "containss":
        if (typeof value === "string") {
          conditions.push(`FIND("${value}", {${field}}) > 0`);
        }
        break;

      case "startswith":
      case "startswiths":
        if (typeof value === "string") {
          conditions.push(`LEFT({${field}}, ${value.length}) = "${value}"`);
        }
        break;

      case "gt":
        if (typeof value === "number") {
          conditions.push(`{${field}} > ${value}`);
        }
        break;

      case "gte":
        if (typeof value === "number") {
          conditions.push(`{${field}} >= ${value}`);
        }
        break;

      case "lt":
        if (typeof value === "number") {
          conditions.push(`{${field}} < ${value}`);
        }
        break;

      case "lte":
        if (typeof value === "number") {
          conditions.push(`{${field}} <= ${value}`);
        }
        break;

      case "in":
        if (Array.isArray(value) && value.length > 0) {
          const orConditions = value.map((v) => {
            if (typeof v === "string") {
              return `{${field}} = "${v}"`;
            }
            return `{${field}} = ${v}`;
          });
          conditions.push(`OR(${orConditions.join(", ")})`);
        }
        break;

      // Add more operators as needed
      default:
        console.warn(`Unsupported filter operator: ${operator}`);
    }
  }

  if (conditions.length === 0) {
    return undefined;
  }

  if (conditions.length === 1) {
    return conditions[0];
  }

  return `AND(${conditions.join(", ")})`;
}

/**
 * Convert Refine sort to Airtable format
 * Note: Airtable API sort is applied server-side via select params
 */
function buildSortParam(sorters?: CrudSort[]): string | undefined {
  if (!sorters || sorters.length === 0) {
    return undefined;
  }

  // Airtable format: field:direction
  return sorters.map((s) => `${s.field}:${s.order}`).join(",");
}

/**
 * Build URL with query parameters
 */
function buildUrl(
  base: string,
  params: Record<string, string | number | undefined>
): string {
  const url = new URL(base, window.location.origin);

  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") {
      url.searchParams.set(key, String(value));
    }
  }

  return url.toString();
}

// ============================================
// Data Provider Implementation
// ============================================

/**
 * Transform Airtable record to a plain object with id and fields spread
 * Applies field normalization for Call Data table to convert Airtable
 * field names (e.g., "Customer Name") to camelCase (e.g., "customerName")
 *
 * @param record - Airtable record with id, fields, and createdTime
 * @param resource - Optional resource name for table-specific normalization
 */
function transformRecord<T extends BaseRecord>(
  record: AirtableRecord,
  resource?: string
): T {
  // Apply field normalization for Call Data table
  const fields = resource && isCallDataTable(resource)
    ? normalizeCallDataFields(record.fields)
    : record.fields;

  return {
    id: record.id,
    ...fields,
    _createdTime: record.createdTime,
  } as unknown as T;
}

/**
 * Custom Refine data provider using serverless API
 * Implements all required DataProvider methods
 *
 * Note: We use type assertion on the entire provider because Refine's
 * DataProvider interface uses generics (TData extends BaseRecord) that
 * cannot be satisfied at compile time when the actual types come from
 * dynamic API responses. This is the standard pattern used by other
 * Refine data providers including @refinedev/airtable.
 */
export const dataProvider = {
  /**
   * Get the API base URL
   */
  getApiUrl: () => API_BASE_URL,

  /**
   * Fetch a list of records with pagination and filtering
   * Supports cursor-based pagination for useInfiniteList
   */
  getList: async ({ resource, pagination, filters, sorters, meta }: {
    resource: string;
    pagination?: { pageSize?: number; currentPage?: number | string };
    filters?: CrudFilter[];
    sorters?: CrudSort[];
    meta?: { cursor?: string };
  }) => {
    const { pageSize = 25, currentPage } = pagination || {};
    // For cursor-based pagination (useInfiniteList), Refine passes the cursor string via pagination.currentPage
    // Check meta.cursor first for explicit cursor, then pagination.currentPage if it's a string (cursor value)
    const cursor = meta?.cursor || (typeof currentPage === 'string' ? currentPage : undefined);

    // Build query parameters
    const params: Record<string, string | number | undefined> = {
      pageSize,
      filter: filtersToFormula(filters),
      sort: buildSortParam(sorters),
      offset: cursor,
    };

    const url = buildUrl(`${API_BASE_URL}/${encodeURIComponent(resource)}`, params);

    const response = await fetchWithAuth<ListResponse>(url);

    return {
      data: response.data.map((record) => transformRecord(record, resource)),
      total: response.total,
      cursor: response.offset ? { next: response.offset } : undefined,
    };
  },

  /**
   * Fetch a single record by ID
   */
  getOne: async ({ resource, id }: { resource: string; id: string | number }) => {
    const url = `${API_BASE_URL}/${encodeURIComponent(resource)}/${encodeURIComponent(String(id))}`;

    const response = await fetchWithAuth<SingleResponse>(url);

    return {
      data: transformRecord(response.data, resource),
    };
  },

  /**
   * Fetch multiple records by IDs
   */
  getMany: async ({ resource, ids }: { resource: string; ids: (string | number)[] }) => {
    if (!ids || ids.length === 0) {
      return { data: [] };
    }

    const url = buildUrl(`${API_BASE_URL}/${encodeURIComponent(resource)}/many`, {
      ids: ids.map(String).join(","),
    });

    const response = await fetchWithAuth<ManyResponse>(url);

    return {
      data: response.data.map((record) => transformRecord(record, resource)),
    };
  },

  /**
   * Create a new record
   */
  create: async ({ resource, variables }: { resource: string; variables: Record<string, unknown> }) => {
    const url = `${API_BASE_URL}/${encodeURIComponent(resource)}`;

    const response = await fetchWithAuth<SingleResponse>(url, {
      method: "POST",
      body: JSON.stringify({ fields: variables }),
    });

    return {
      data: transformRecord(response.data, resource),
    };
  },

  /**
   * Update an existing record
   */
  update: async ({ resource, id, variables }: {
    resource: string;
    id: string | number;
    variables: Record<string, unknown>;
  }) => {
    const url = `${API_BASE_URL}/${encodeURIComponent(resource)}/${encodeURIComponent(String(id))}`;

    const response = await fetchWithAuth<SingleResponse>(url, {
      method: "PATCH",
      body: JSON.stringify({ fields: variables }),
    });

    return {
      data: transformRecord(response.data, resource),
    };
  },

  /**
   * Delete a record by ID
   */
  deleteOne: async ({ resource, id }: { resource: string; id: string | number }) => {
    const url = `${API_BASE_URL}/${encodeURIComponent(resource)}/${encodeURIComponent(String(id))}`;

    const response = await fetchWithAuth<DeleteResponse>(url, {
      method: "DELETE",
    });

    return {
      data: { id: response.id },
    };
  },

  /**
   * Custom method - not required but can be useful
   */
  custom: async ({ url, method, payload, headers }: {
    url: string;
    method?: string;
    payload?: unknown;
    headers?: Record<string, string>;
  }) => {
    const fullUrl = url.startsWith("http") ? url : `${API_BASE_URL}${url}`;

    const response = await fetchWithAuth<BaseRecord>(fullUrl, {
      method: method || "GET",
      body: payload ? JSON.stringify(payload) : undefined,
      headers,
    });

    return { data: response };
  },
} as DataProvider;

/**
 * React Query cache options for the data provider
 * Configured for reasonable caching with Airtable
 */
export const dataProviderQueryOptions = {
  staleTime: 30000, // 30 seconds - data considered fresh
  gcTime: 300000, // 5 minutes - cache garbage collection
  retry: 3,
  retryDelay: (attemptIndex: number) =>
    Math.min(1000 * 2 ** attemptIndex, 30000),
};
